/**
 * Logistics Dialog Validation Schemas
 *
 * Validation for updating real waste collection values.
 * Used by LOGISTIC users to record actual collected amounts and upload process documents.
 *
 * Created: 2025-11-02 (Phase 5 - Admin Forms Refactoring)
 * Updated: 2025-11-02 (Phase 1 - Shared Validation Schemas)
 */

import { z } from 'zod';
import { fileUrlSchema, atLeastOneGreaterThanZero } from '@/shared/validation/common.schemas';

/**
 * Logistics Update Schema
 *
 * Validates real waste values and optional process documents.
 * Enforces business rule: at least one waste type must have value > 0.
 *
 * Fields:
 * - realDeeeKg: Real DEEE waste collected (kg, non-negative)
 * - realDbaKg: Real DBA waste collected (kg, non-negative)
 * - realBecuriSiNeoaneKg: Real light bulbs collected (kg, non-negative)
 * - deeeOrderDocument: DEEE process document URL (optional)
 * - dbaOrderDocument: DBA process document URL (optional)
 */
export const logisticsSchema = z
  .object({
    /**
     * Real DEEE waste collected (kg)
     * Must be non-negative
     */
    realDeeeKg: z.coerce
      .number({
        required_error: 'Valoarea DEEE este obligatorie',
        invalid_type_error: 'Valoarea DEEE trebuie să fie un număr',
      })
      .min(0, 'Valoarea DEEE trebuie să fie pozitivă'),

    /**
     * Real DBA waste collected (kg)
     * Must be non-negative
     */
    realDbaKg: z.coerce
      .number({
        required_error: 'Valoarea DBA este obligatorie',
        invalid_type_error: 'Valoarea DBA trebuie să fie un număr',
      })
      .min(0, 'Valoarea DBA trebuie să fie pozitivă'),

    /**
     * Real light bulbs & neon tubes collected (kg)
     * Must be non-negative
     */
    realBecuriSiNeoaneKg: z.coerce
      .number({
        required_error: 'Valoarea Becuri & Neoane este obligatorie',
        invalid_type_error: 'Valoarea Becuri & Neoane trebuie să fie un număr',
      })
      .min(0, 'Valoarea Becuri & Neoane trebuie să fie pozitivă'),

    /**
     * DEEE process document URL
     * Optional - uploaded file URL from server
     */
    deeeOrderDocument: fileUrlSchema,

    /**
     * DBA process document URL
     * Optional - uploaded file URL from server
     */
    dbaOrderDocument: fileUrlSchema,
  })
  .refine(
    atLeastOneGreaterThanZero(['realDeeeKg', 'realDbaKg', 'realBecuriSiNeoaneKg']),
    {
      message: 'Cel puțin o valoare trebuie să fie diferită de zero',
      path: ['realDeeeKg'], // Error shows on first field
    }
  );

/**
 * TypeScript type inferred from Zod schema
 * Use this type for form data in components
 */
export type LogisticsFormData = z.infer<typeof logisticsSchema>;
